import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Instagram, Facebook } from "lucide-react";

function getWeekDays(date) {
  const start = new Date(date);
  start.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

function getMonthDays(date) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = firstDay.getDay();
  const days = [];
  for (let i = startPad - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, current: false });
  }
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({ date: new Date(year, month, i), current: true });
  }
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({ date: new Date(year, month + 1, i), current: false });
  }
  return days;
}

function PostPill({ post }) {
  return (
    <div
      className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] truncate cursor-pointer hover:opacity-80 transition-opacity"
      style={{
        background: `${post.brand?.color_hex || "#c6a96f"}15`,
        color: post.brand?.color_hex || "#c6a96f",
        borderLeft: `2px solid ${post.brand?.color_hex || "#c6a96f"}`,
      }}
      data-testid={`calendar-post-${post.id}`}
    >
      {post.platforms?.includes("instagram") && <Instagram size={10} />}
      {post.platforms?.includes("facebook") && <Facebook size={10} />}
      <span className="truncate font-mono">{post.brand?.short_name}</span>
      <span className="truncate opacity-70">{new Date(post.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
    </div>
  );
}

export default function ContentCalendar() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("month");

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const start = new Date(currentDate);
      const end = new Date(currentDate);
      if (view === "week") {
        start.setDate(start.getDate() - start.getDay());
        end.setDate(start.getDate() + 7);
      } else {
        start.setDate(1);
        end.setMonth(end.getMonth() + 1, 0);
      }
      const res = await api.get("/calendar", {
        params: { start: start.toISOString(), end: end.toISOString() },
      });
      setPosts(res.data.posts);
    } catch { toast.error("Failed to load calendar"); }
    setLoading(false);
  };

  useEffect(() => { fetchPosts(); }, [currentDate, view]);

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (view === "week") d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
  };

  const postsByDate = useMemo(() => {
    const map = {};
    posts.forEach((p) => {
      if (p.scheduled_at) {
        const key = new Date(p.scheduled_at).toDateString();
        if (!map[key]) map[key] = [];
        map[key].push(p);
      }
    });
    return map;
  }, [posts]);

  const today = new Date().toDateString();
  const days = view === "week" ? getWeekDays(currentDate) : getMonthDays(currentDate);
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="content-calendar">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-zinc-100">Calendar</h1>
          <p className="section-label mt-2">// content schedule</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center bg-studio-surface border border-[#2a2e36] rounded-xl overflow-hidden">
            <button
              onClick={() => setView("week")}
              className={`px-4 py-2 text-xs font-medium transition-colors ${view === "week" ? "bg-gold/10 text-gold" : "text-zinc-400 hover:text-zinc-300"}`}
              data-testid="calendar-view-week"
            >Week</button>
            <button
              onClick={() => setView("month")}
              className={`px-4 py-2 text-xs font-medium transition-colors ${view === "month" ? "bg-gold/10 text-gold" : "text-zinc-400 hover:text-zinc-300"}`}
              data-testid="calendar-view-month"
            >Month</button>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)} className="p-2 rounded-lg border border-[#363b45] text-zinc-400 hover:text-gold transition-colors" data-testid="calendar-prev">
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm text-zinc-200 font-medium min-w-[140px] text-center">
              {currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
            </span>
            <button onClick={() => navigate(1)} className="p-2 rounded-lg border border-[#363b45] text-zinc-400 hover:text-gold transition-colors" data-testid="calendar-next">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="spinner-gold" /></div>
      ) : (
        <div className="bg-studio-raised border border-[#2a2e36] rounded-xl overflow-hidden">
          <div className="grid grid-cols-7 border-b border-[#222630]">
            {dayNames.map((d) => (
              <div key={d} className="p-3 text-center">
                <span className="section-label">{d}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {(view === "week" ? days.map((d) => ({ date: d, current: true })) : days).map(({ date, current }, i) => {
              const key = date.toDateString();
              const isToday = key === today;
              const dayPosts = postsByDate[key] || [];

              return (
                <div
                  key={i}
                  className={`min-h-[100px] p-2 border-b border-r border-[#222630] ${
                    !current ? "opacity-30" : ""
                  } ${isToday ? "ring-1 ring-inset ring-gold/30" : ""}`}
                >
                  <span className={`text-xs font-mono ${isToday ? "text-gold font-bold" : "text-zinc-400"}`}>
                    {date.getDate()}
                  </span>
                  <div className="mt-1 space-y-1">
                    {dayPosts.map((p) => (
                      <PostPill key={p.id} post={p} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
